import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useBranding, contrastWarning } from "@/context/BrandingContext";
import { can, ROLE_LABELS } from "@/lib/permissions";
import { PageHeader, SectionCard } from "@/components/common/Primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { fmtEUR } from "@/lib/format";
import {
  Palette, Package, Users as UsersIcon, Bot, AlertTriangle, RotateCcw, Save,
  Plus, Loader2, Upload, Check,
} from "lucide-react";

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = can(user?.role, "settings:write");
  return (
    <div>
      <PageHeader title="Einstellungen" description="Organisation, Erscheinungsbild, Tarif, Module und Benutzer" />
      <Tabs defaultValue="branding">
        <TabsList className="flex-wrap">
          <TabsTrigger value="branding" data-testid="settings-tab-branding"><Palette className="mr-1.5 h-4 w-4" />Erscheinungsbild</TabsTrigger>
          <TabsTrigger value="plan" data-testid="settings-tab-plan"><Package className="mr-1.5 h-4 w-4" />Tarif & Module</TabsTrigger>
          <TabsTrigger value="users" data-testid="settings-tab-users"><UsersIcon className="mr-1.5 h-4 w-4" />Benutzer</TabsTrigger>
          <TabsTrigger value="ai" data-testid="settings-tab-ai"><Bot className="mr-1.5 h-4 w-4" />KI</TabsTrigger>
        </TabsList>
        <TabsContent value="branding" className="mt-4"><BrandingTab canEdit={can(user?.role, "branding:write")} /></TabsContent>
        <TabsContent value="plan" className="mt-4"><PlanTab canEdit={isAdmin} isOwner={user?.role === "owner"} /></TabsContent>
        <TabsContent value="users" className="mt-4"><UsersTab canEdit={can(user?.role, "users:write")} /></TabsContent>
        <TabsContent value="ai" className="mt-4"><AITab canEdit={isAdmin} /></TabsContent>
      </Tabs>
    </div>
  );
}

function ColorField({ label, value, onChange }) {
  const warn = value && contrastWarning(value);
  return (
    <div className="space-y-1.5">
      <Label className="flex items-center gap-2">{label}
        {warn && <span className="flex items-center gap-1 text-[10px] font-normal text-amber-600"><AlertTriangle className="h-3 w-3" />Kontrast prüfen</span>}</Label>
      <div className="flex items-center gap-2">
        <span className="h-9 w-9 shrink-0 rounded-md border border-border" style={{ background: `hsl(${value})` }} />
        <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="222 47% 15%" className="font-mono text-xs" />
      </div>
    </div>
  );
}

function BrandingTab({ canEdit }) {
  const { refreshTenant } = useAuth();
  const { applyPreview, dark } = useBranding();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["branding"], queryFn: async () => (await api.get("/settings/branding")).data });
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (data?.branding) setForm(data.branding); }, [data]);
  useEffect(() => { if (form) applyPreview(form, dark); }, [form, dark, applyPreview]);

  if (!form) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 512 * 1024) return toast.error("Logo zu groß (max. 512 KB)");
    if (!["image/svg+xml", "image/png", "image/webp"].includes(file.type)) return toast.error("Nur SVG, PNG oder WebP");
    const reader = new FileReader();
    reader.onload = () => set("logo_data", reader.result);
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try { await api.put("/settings/branding", form); await refreshTenant(); toast.success("Branding gespeichert"); qc.invalidateQueries({ queryKey: ["branding"] }); }
    catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  const reset = async () => {
    try { const { data: r } = await api.post("/settings/branding/reset"); setForm(r.branding); await refreshTenant(); toast.success("Auf Standard zurückgesetzt"); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <SectionCard title="Erscheinungsbild & Branding">
        {!canEdit && <p className="mb-3 rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">Nur Administratoren können das Branding bearbeiten.</p>}
        <fieldset disabled={!canEdit} className="space-y-4">
          <div className="space-y-1.5"><Label>Anzeigename (optional)</Label>
            <Input value={form.company_display_name || ""} onChange={(e) => set("company_display_name", e.target.value)} placeholder="Firmenname in der Navigation" data-testid="branding-name-input" /></div>

          <div className="space-y-1.5"><Label>Firmenlogo (SVG, PNG, WebP · max. 512 KB)</Label>
            <div className="flex items-center gap-3">
              {form.logo_data ? <img src={form.logo_data} alt="Logo" className="h-10 w-10 rounded-md border border-border object-contain" /> :
                <span className="flex h-10 w-10 items-center justify-center rounded-md border border-dashed border-border"><Upload className="h-4 w-4 text-muted-foreground" /></span>}
              <Input type="file" accept="image/svg+xml,image/png,image/webp" onChange={onLogo} data-testid="branding-logo-input" />
              {form.logo_data && <Button variant="ghost" size="sm" onClick={() => set("logo_data", null)}>Entfernen</Button>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <ColorField label="Primärfarbe (hell)" value={form.primary} onChange={(v) => set("primary", v)} />
            <ColorField label="Akzent (hell)" value={form.accent} onChange={(v) => set("accent", v)} />
            <ColorField label="Primärfarbe (dunkel)" value={form.primary_dark} onChange={(v) => set("primary_dark", v)} />
            <ColorField label="Akzent (dunkel)" value={form.accent_dark} onChange={(v) => set("accent_dark", v)} />
          </div>

          <div className="space-y-1.5"><Label>Design-Modus</Label>
            <Select value={form.theme_mode} onValueChange={(v) => set("theme_mode", v)}>
              <SelectTrigger data-testid="branding-theme-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="system">Systemstandard</SelectItem>
                <SelectItem value="light">Immer hell</SelectItem>
                <SelectItem value="dark">Immer dunkel</SelectItem>
              </SelectContent>
            </Select></div>

          {canEdit && (
            <div className="flex flex-wrap gap-2">
              <Button onClick={save} className="bg-accent text-accent-foreground hover:bg-accent/90" disabled={saving} data-testid="branding-save-button">
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Speichern
              </Button>
              <Button variant="outline" onClick={reset} data-testid="branding-reset-button"><RotateCcw className="mr-2 h-4 w-4" />Standard</Button>
            </div>
          )}
        </fieldset>
      </SectionCard>

      <SectionCard title="Live-Vorschau">
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">Änderungen sind sofort sichtbar und werden erst nach dem Speichern dauerhaft übernommen.</p>
          <div className="rounded-lg border border-border p-4">
            <div className="mb-3 flex items-center gap-2"><span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent text-accent-foreground">{form.logo_data ? <img src={form.logo_data} alt="" className="h-5 w-5 object-contain" /> : "PN"}</span><span className="text-sm font-semibold">{form.company_display_name || "primeNetConnect"}</span></div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" className="bg-primary text-primary-foreground">Primär</Button>
              <Button size="sm" className="bg-accent text-accent-foreground">Akzent</Button>
              <Badge className="bg-emerald-500/10 text-emerald-700">Aktiv</Badge>
              <Badge className="bg-amber-500/10 text-amber-700">Ausstehend</Badge>
            </div>
            <div className="mt-3 overflow-hidden rounded-md border border-border">
              <div className="flex items-center justify-between bg-muted/40 px-3 py-2 text-xs font-semibold text-muted-foreground"><span>Vertrag</span><span>Status</span></div>
              <div className="flex items-center justify-between px-3 py-2 text-sm"><span>Telekom · MagentaMobil</span><Badge className="bg-accent/15 text-accent">Verlängerung</Badge></div>
            </div>
          </div>
        </div>
      </SectionCard>
    </div>
  );
}

function PlanTab({ canEdit, isOwner }) {
  const { refreshTenant } = useAuth();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["tenant-settings"], queryFn: async () => (await api.get("/settings/tenant")).data });
  const { data: reg } = useQuery({ queryKey: ["registry"], queryFn: async () => (await api.get("/settings/registry")).data });
  if (!data || !reg) return null;
  const tenant = data.tenant; const plan = data.plan; const usage = data.usage;

  const changePlan = async (p) => {
    try { await api.post("/settings/plan", { plan: p }); await refreshTenant(); toast.success("Tarif geändert"); qc.invalidateQueries({ queryKey: ["tenant-settings"] }); }
    catch (err) { toast.error(apiError(err)); }
  };
  const toggleModule = async (mid, enabled) => {
    try { await api.post("/settings/modules", { module_id: mid, enabled }); await refreshTenant(); toast.success("Modul aktualisiert"); qc.invalidateQueries({ queryKey: ["tenant-settings"] }); }
    catch (err) { toast.error(apiError(err)); }
  };

  const UsageRow = ({ label, used, max }) => (
    <div className="space-y-1"><div className="flex justify-between text-xs"><span className="text-muted-foreground">{label}</span><span className="font-medium">{used}{max ? ` / ${max}` : ""}</span></div>
      {max ? <Progress value={Math.min(100, (used / max) * 100)} className="h-1.5" /> : null}</div>
  );

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <SectionCard title="Aktueller Tarif">
        <div className="mb-4 flex items-end justify-between">
          <div><div className="text-2xl font-semibold">{plan.name}</div><div className="text-sm text-muted-foreground">{fmtEUR(plan.price_eur)} / Monat</div></div>
          <Badge className="bg-accent/15 text-accent capitalize">{tenant.plan}</Badge>
        </div>
        <div className="space-y-3">
          <UsageRow label="Benutzer" used={usage.users} max={plan.max_users} />
          <UsageRow label="Kunden" used={usage.customers} max={null} />
          <UsageRow label="Verträge" used={usage.contracts} max={null} />
          <UsageRow label="Dokumente" used={usage.documents} max={null} />
          <UsageRow label="Speicher (MB)" used={0} max={plan.max_storage_mb} />
          <UsageRow label="KI-Anfragen / Monat" used={0} max={plan.ai_requests_per_month} />
        </div>
        <p className="mt-4 text-[11px] text-muted-foreground">Drittanbieterkosten (z. B. WhatsApp-Nachrichten) können separat abgerechnet werden. Es werden im MVP keine echten Zahlungen erhoben.</p>
      </SectionCard>

      <SectionCard title="Tarif wechseln">
        <div className="space-y-2">
          {reg.plans.map((p) => (
            <div key={p.key} className={`flex items-center justify-between rounded-md border px-4 py-3 ${p.key === tenant.plan ? "border-accent bg-accent/5" : "border-border"}`}>
              <div><div className="text-sm font-medium">{p.name} · {fmtEUR(p.price_eur)}/M</div><div className="text-xs text-muted-foreground">{p.max_users} Benutzer · {p.modules.join(", ")}</div></div>
              {p.key === tenant.plan ? <Badge variant="secondary"><Check className="mr-1 h-3 w-3" />Aktiv</Badge> :
                isOwner ? <Button size="sm" variant="outline" onClick={() => changePlan(p.key)} data-testid={`select-plan-${p.key}`}>Wählen</Button> : null}
            </div>
          ))}
          {!isOwner && <p className="text-[11px] text-muted-foreground">Nur der Inhaber kann den Tarif ändern.</p>}
        </div>
      </SectionCard>

      <SectionCard title="Branchenmodule" className="lg:col-span-2">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {reg.modules.map((m) => {
            const enabled = tenant.modules.includes(m.id);
            const allowed = plan.modules.includes(m.id);
            const available = m.status === "available";
            return (
              <div key={m.id} className="rounded-md border border-border p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold">{m.name}</span>
                  <Switch checked={enabled} disabled={!canEdit || !allowed || !available} onCheckedChange={(v) => toggleModule(m.id, v)} data-testid={`module-toggle-${m.id}`} />
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{m.description}</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {!available && <Badge variant="outline" className="text-[10px]">Bald verfügbar</Badge>}
                  {!allowed && available && <Badge variant="outline" className="text-[10px]">Im Tarif upgraden</Badge>}
                  <Badge variant="secondary" className="text-[10px]">v{m.version}</Badge>
                </div>
              </div>
            );
          })}
        </div>
      </SectionCard>
    </div>
  );
}

function UsersTab({ canEdit }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["users"], queryFn: async () => (await api.get("/settings/users")).data });
  const { data: reg } = useQuery({ queryKey: ["registry"], queryFn: async () => (await api.get("/settings/registry")).data });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "sales", password: "" });
  const [busy, setBusy] = useState(false);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data: r } = await api.post("/settings/users", form);
      if (r.temp_password) toast.success(`Benutzer angelegt. Temporäres Passwort: ${r.temp_password}`, { duration: 10000 });
      else toast.success("Benutzer angelegt");
      setOpen(false); setForm({ email: "", name: "", role: "sales", password: "" });
      qc.invalidateQueries({ queryKey: ["users"] });
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const toggleActive = async (u) => {
    try { await api.put(`/settings/users/${u.id}`, { active: !u.active }); qc.invalidateQueries({ queryKey: ["users"] }); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <SectionCard title="Benutzer & Rollen" action={canEdit && <Button size="sm" className="bg-accent text-accent-foreground hover:bg-accent/90" onClick={() => setOpen(true)} data-testid="new-user-button"><Plus className="mr-1.5 h-4 w-4" />Benutzer</Button>}>
      <div className="space-y-2">
        {(data?.items || []).map((u) => (
          <div key={u.id} className="flex items-center justify-between gap-3 rounded-md border border-border px-4 py-3">
            <div className="min-w-0"><div className="truncate text-sm font-medium">{u.name}</div><div className="truncate text-xs text-muted-foreground">{u.email}</div></div>
            <div className="flex shrink-0 items-center gap-2">
              <Badge variant="secondary">{u.role_label || ROLE_LABELS[u.role]}</Badge>
              {u.role !== "owner" && canEdit && <Switch checked={u.active} onCheckedChange={() => toggleActive(u)} data-testid={`user-active-${u.id}`} />}
            </div>
          </div>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Neuer Benutzer</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required data-testid="user-name-input" /></div>
            <div className="space-y-1.5"><Label>E-Mail</Label><Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} required data-testid="user-email-input" /></div>
            <div className="space-y-1.5"><Label>Rolle</Label>
              <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{(reg?.roles || []).filter((r) => r.key !== "owner").map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>Passwort (optional)</Label><Input type="text" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} placeholder="Leer = automatisch" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
              <Button type="submit" className="bg-accent text-accent-foreground hover:bg-accent/90" disabled={busy} data-testid="user-save-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Anlegen</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
}

function AITab({ canEdit }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["ai-config"], queryFn: async () => (await api.get("/ai/config")).data });
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (data?.ai) setForm(data.ai); }, [data]);
  if (!form) return null;

  const save = async () => {
    setBusy(true);
    try { await api.put("/ai/config", { mode: form.mode, endpoint: form.endpoint, model_name: form.model_name }); toast.success("KI-Konfiguration gespeichert"); qc.invalidateQueries({ queryKey: ["ai-config"] }); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <SectionCard title="Private KI">
      <fieldset disabled={!canEdit} className="max-w-lg space-y-4">
        <div className="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
          Striktes Privatmodus-Verhalten: Ohne erreichbaren lokalen Endpunkt bleibt die KI „nicht konfiguriert". Es wird niemals auf einen externen Anbieter ausgewichen.
        </div>
        <div className="space-y-1.5"><Label>Betriebsmodus</Label>
          <Select value={form.mode} onValueChange={(v) => setForm((f) => ({ ...f, mode: v }))}>
            <SelectTrigger data-testid="ai-mode-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="not_configured">Nicht konfiguriert</SelectItem>
              <SelectItem value="private">Privat (lokaler Endpunkt)</SelectItem>
            </SelectContent>
          </Select></div>
        <div className="space-y-1.5"><Label>Modell-Endpunkt (lokal)</Label><Input value={form.endpoint || ""} onChange={(e) => setForm((f) => ({ ...f, endpoint: e.target.value }))} placeholder="http://localhost:11434" data-testid="ai-endpoint-input" /></div>
        <div className="space-y-1.5"><Label>Modellname</Label><Input value={form.model_name || ""} onChange={(e) => setForm((f) => ({ ...f, model_name: e.target.value }))} placeholder="z. B. llama3.1" /></div>
        {canEdit && <Button onClick={save} className="bg-accent text-accent-foreground hover:bg-accent/90" disabled={busy} data-testid="ai-config-save-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Speichern</Button>}
      </fieldset>
    </SectionCard>
  );
}
